import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Container } from '@/components/layout/Container';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { zodResolver } from '@/utils/zodResolver';
import { useAuth } from '../AuthProvider';
import { confirmSignUp, describeAuthError, resendCode, signUp } from '../cognito';

// Mirrors the Cognito pool policy so the user hears about it before a round trip.
const registerSchema = z.object({
  name: z.string().trim().min(2, 'Enter your full name').max(100),
  email: z.string().trim().email('Enter a valid email address'),
  password: z.string()
    .min(10, 'Use at least 10 characters')
    .regex(/[a-z]/, 'Include a lower case letter')
    .regex(/[A-Z]/, 'Include an upper case letter')
    .regex(/\d/, 'Include a number'),
});
const codeSchema = z.object({ code: z.string().trim().min(4, 'Enter the code from your email') });
type RegisterValues = z.infer<typeof registerSchema>;
type CodeValues = z.infer<typeof codeSchema>;

export function RegisterPage() {
  const [pending, setPending] = useState<{ email: string; password: string } | null>(null);
  return (
    <Container className="max-w-md">
      <PageHeader
        title={pending ? 'Verify your email' : 'Create an account'}
        description={pending ? `We sent a code to ${pending.email}.` : 'One account for offering rides and booking seats.'}
      />
      <Card><CardBody>{pending ? <ConfirmStep {...pending} /> : <DetailsStep onDone={setPending} />}</CardBody></Card>
      {!pending && <p className="mt-4 text-center text-sm text-slate-600">
        Already registered? <Link className="text-brand-700 hover:underline" to="/login">Log in</Link></p>}
    </Container>
  );
}

function DetailsStep({ onDone }: { onDone: (v: { email: string; password: string }) => void }) {
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState: { errors, isSubmitting } } =
    useForm<RegisterValues>({ resolver: zodResolver<RegisterValues>(registerSchema) });

  const onSubmit = handleSubmit(async ({ name, email, password }) => {
    setError(null);
    try {
      await signUp(email, password, name);
      onDone({ email, password });
    } catch (e) { setError(describeAuthError(e)); }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4" aria-label="Create an account">
      {error && <Alert tone="error">{error}</Alert>}
      <Field label="Full name" autoComplete="name" error={errors.name?.message} {...register('name')} />
      <Field label="Email" type="email" autoComplete="email" error={errors.email?.message} {...register('email')} />
      <Field label="Password" type="password" autoComplete="new-password"
        hint="At least 10 characters, with upper case, lower case and a number."
        error={errors.password?.message} {...register('password')} />
      <Button type="submit" size="lg" className="w-full" loading={isSubmitting}>Create account</Button>
    </form>
  );
}

function ConfirmStep({ email, password }: { email: string; password: string }) {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const { register, handleSubmit, formState: { errors, isSubmitting } } =
    useForm<CodeValues>({ resolver: zodResolver<CodeValues>(codeSchema) });

  const onSubmit = handleSubmit(async ({ code }) => {
    setError(null);
    try {
      await confirmSignUp(email, code);
      await signIn(email, password); // straight into the app after verifying
      navigate('/', { replace: true });
    } catch (e) { setError(describeAuthError(e)); }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4" aria-label="Verify your email">
      {error && <Alert tone="error">{error}</Alert>}
      {info && <Alert tone="success">{info}</Alert>}
      <Field label="Verification code" inputMode="numeric" autoComplete="one-time-code"
        error={errors.code?.message} {...register('code')} />
      <Button type="submit" size="lg" className="w-full" loading={isSubmitting}>Verify and continue</Button>
      <Button type="button" variant="ghost" className="w-full" onClick={async () => {
        try { await resendCode(email); setInfo('A new code is on its way.'); } catch (e) { setError(describeAuthError(e)); }
      }}>Send a new code</Button>
    </form>
  );
}
